<script setup lang="ts">
/**
 * This is a simple source code editor component that uses ace editor for highlighting, prettyfying and autocomplete code
 * It allows you to edit source code with syntax highlighting.
 *
 * @see https://ace.c9.io/#api=editor&nav=embedding
 * @see https://github.com/CarterLi/vue3-ace-editor
 *
 */
import { VAceEditor } from 'vue3-ace-editor';
import 'ace-builds/src-noconflict/mode-json'; // Load the language definition file used below
import 'ace-builds/src-noconflict/mode-css'; // Load the language definition file used below
import 'ace-builds/src-noconflict/mode-html'; // Load the language definition file used below
import 'ace-builds/src-noconflict/mode-javascript'; // Load the language definition file used below
import 'ace-builds/src-noconflict/theme-terminal'; // Load the theme definition file used below
import 'ace-builds/src-noconflict/theme-xcode'; // Load the theme definition file used below
import 'ace-builds/src-noconflict/ext-language_tools'; // Load the language tools used below
import { computed, ref, watch } from 'vue';
import { useThemeStoreRef } from '@/stores';

const options = {
    enableLiveAutocompletion: true,
    enableBasicAutocompletion: true,
    useWorker: false,
};

const props = withDefaults(
    defineProps<{
        modelValue: string | null;
        /**
         * The highlight rules to use
         * @default 'html'
         */
        language?: 'html' | 'css' | 'json' | 'javascript';

        /**
         * The initial lines to show if the editor is empty
         * @default 2
         */
        initiallines?: number;

        /**
         * Show line numbers
         * @default true
         */
        linenumbers?: boolean;
    }>(),
    {
        language: 'html',
        initiallines: 2,
        linenumbers: true,
    },
);

const value = computed<string>({
    get: () => props.modelValue ?? '',
    set: (val: string) => emit('update:modelValue', val),
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
}>();

const { currentTheme } = useThemeStoreRef();

const theme = ref<'terminal' | 'xcode'>('terminal');

watch(
    () => currentTheme.value!,
    (nv) => (nv == 'dark' ? (theme.value = 'terminal') : (theme.value = 'xcode')),
    { immediate: true },
);

const onInit = (editor: any) => {
    editor.setOptions({
        fontSize: '10pt',
    });
};

const editor = ref(null);
</script>

<template>
    <div class="form-control-sourceeditor flex-fill">
        <v-ace-editor
            ref="editor"
            :theme="theme"
            v-model:value="value"
            :lang="props.language"
            :min-lines="initiallines"
            :max-lines="initiallines"
            @init="onInit"
            :options="options"
        />
    </div>
</template>
